import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Heading, Text, SimpleGrid, HStack, VStack, Grid, GridItem,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Icon,
} from '@chakra-ui/react';
import { FiInfo, FiArrowDown, FiArrowUp } from 'react-icons/fi';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import PermissionPreview from '../components/ui/PermissionPreview';
import TrendChart from '../components/SampleBarChart';
import client from '../utils/fetchClient';
import { useProjectContext } from '../context/ProjectContext';
import { useOrg } from '../context/OrgContext';

const fmtDuration = (ms) => {
  if (!ms || Number.isNaN(ms)) return '-';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

const ACCESS_COPY = {
  owner: {
    text: 'You have Owner access — full control of this organisation, including billing and ownership. Test data is ingested from CI and is read-only for everyone.',
    bg: 'brand.50', border: 'brand.100', icon: 'brand.600',
  },
  admin: {
    text: 'You have Admin access — you can manage projects, members, API keys and integrations. Test data is ingested from CI and is read-only for everyone.',
    bg: 'info.50', border: 'info.100', icon: 'info.500',
  },
  developer: {
    text: 'You have Developer access — full visibility into every project’s test results, and read-only on organisation settings. Test data is never editable by anyone.',
    bg: 'gray.100', border: 'gray.200', icon: 'gray.500',
  },
};

function Delta({ current, previous, lowerIsBetter = false }) {
  if (previous == null || current == null || current === previous) {
    return <Text as="span" fontSize="xs" color="gray.400">no change</Text>;
  }
  const up = current > previous;
  const good = lowerIsBetter ? !up : up;
  return (
    <Text as="span" fontSize="xs" fontWeight="700" color={good ? 'status.pass' : 'status.fail'} display="inline-flex" alignItems="center" gap="2px">
      <Icon as={up ? FiArrowUp : FiArrowDown} boxSize={3} />
      {Math.abs(Math.round((current - previous) * 10) / 10)}
    </Text>
  );
}

function StatTile({ label, value, children }) {
  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
      <Text fontSize="xs" color="gray.500" mb={1.5}>{label}</Text>
      <HStack align="baseline" spacing={2}>
        <Text fontSize="28px" fontWeight="800" letterSpacing="-0.5px" color="gray.900">{value}</Text>
        {children}
      </HStack>
    </Box>
  );
}

export default function HomePage() {
  const { effectiveProjectParam } = useProjectContext();
  const { role, org } = useOrg();
  const [summary, setSummary] = useState(null);
  const [prev, setPrev] = useState(null);
  const [trend, setTrend] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const query = { period: 'daily' };
        if (effectiveProjectParam) query.project = effectiveProjectParam;
        const [mRes, modRes] = await Promise.all([
          client.get('/api/metrics', { query }),
          client.get('/api/modules', { query: { ...query, page_size: 50 } }),
        ]);
        if (!mRes.ok) throw new Error('Failed to load metrics');
        const m = await mRes.json();
        const mod = modRes.ok ? await modRes.json() : { data: [] };
        if (cancelled) return;
        setSummary(m.summary || null);
        setPrev(m.previousSummary || null);
        setTrend(Array.isArray(m.metrics) ? m.metrics : []);
        setModules(Array.isArray(mod.data) ? mod.data : []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [effectiveProjectParam]);

  const access = ACCESS_COPY[role] || ACCESS_COPY.developer;

  const failingSuites = [...modules]
    .filter((s) => (s.failedCount || 0) > 0)
    .sort((a, b) => (b.failRate || 0) - (a.failRate || 0))
    .slice(0, 6);

  const chartData = trend.map((t) => ({
    period: t.date,
    passed: t.passedTests,
    failed: t.failedTests,
    skipped: t.skippedTests,
  }));

  const recentFailures = (summary?.recentFailures || []).slice(0, 6);

  const passRate = summary?.passRate ?? null;
  const failingCount = failingSuites.length;
  const verdict =
    passRate == null ? { label: 'No data', color: 'gray.500', bg: 'gray.100' }
    : failingCount === 0 && passRate >= 98 ? { label: 'Ready', color: 'status.pass', bg: 'status.passBg' }
    : failingCount >= 3 || passRate < 90 ? { label: 'At risk', color: 'status.flakyText', bg: 'status.flakyBg' }
    : { label: 'Caution', color: 'status.flakyText', bg: 'status.flakyBg' };

  return (
    <Page maxW="7xl">
      <Flex align="flex-end" justify="space-between" gap={6} wrap="wrap" mb={4}>
        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900">Home</Heading>
          <Text fontSize="sm" color="gray.500">{org.name} · overview of automated test health</Text>
        </Box>
        <PermissionPreview />
      </Flex>

      <Flex align="flex-start" gap={2.5} bg={access.bg} border="1px solid" borderColor={access.border} borderRadius="12px" px={4} py={2.5} mb={5}>
        <Icon as={FiInfo} color={access.icon} boxSize={4} mt="2px" flexShrink={0} />
        <Text fontSize="13px" color="gray.700" lineHeight="1.5">{access.text}</Text>
      </Flex>

      {loading && <LoadingState message="Loading overview" />}
      {error && <ErrorState message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
        <VStack align="stretch" spacing={5}>
          {/* Release readiness */}
          <SectionCard>
            <Flex gap={7} align="center" wrap="wrap">
              <Box flex="1" minW="260px">
                <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="0.06em" color="gray.400" mb={2}>
                  Release readiness
                </Text>
                <Box as="span" display="inline-flex" alignItems="center" gap={2} bg={verdict.bg} color={verdict.color} fontWeight="700" fontSize="sm" px={3.5} py={1.5} borderRadius="full">
                  {verdict.label}
                </Box>
                <Text mt={3} fontSize="sm" color="gray.700" maxW="460px" lineHeight="1.55">
                  {failingCount > 0
                    ? `${summary.failedTests} failing tests across ${failingCount} suite${failingCount > 1 ? 's' : ''}. Review the failing suites below before releasing.`
                    : 'No failing suites in the selected period.'}
                </Text>
              </Box>
              <Box w="1px" alignSelf="stretch" bg="gray.200" />
              <Box minW="180px">
                <Text fontSize="xs" color="gray.500" mb={1}>Pass rate</Text>
                <HStack align="baseline" spacing={2}>
                  <Text fontSize="40px" fontWeight="800" letterSpacing="-1px" color="gray.900">
                    {passRate != null ? `${passRate}%` : '-'}
                  </Text>
                  <Delta current={passRate} previous={prev?.passRate} />
                </HStack>
                <Text fontSize="xs" color="gray.400">{summary.totalRuns} runs in period</Text>
              </Box>
            </Flex>
          </SectionCard>

          {/* Stat tiles */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3.5}>
            <StatTile label="Pass rate" value={passRate != null ? `${passRate}%` : '-'}>
              <Delta current={passRate} previous={prev?.passRate} />
            </StatTile>
            <StatTile label="Failing suites" value={failingCount}>
              <Delta current={failingCount} previous={null} lowerIsBetter />
            </StatTile>
            <StatTile label="Flaky rate" value={`${summary.flakiness ?? 0}%`}>
              <Delta current={summary.flakiness} previous={prev?.flakiness} lowerIsBetter />
            </StatTile>
            <StatTile label="Avg duration" value={fmtDuration(summary.totalRuns ? summary.totalDuration / summary.totalRuns : 0)} />
          </SimpleGrid>

          {/* Trend */}
          <SectionCard>
            <Heading size="sm" mb={3} color="gray.900">Test results trend</Heading>
            <Box w="full">
              <TrendChart
                data={chartData}
                series={[
                  { key: 'passed', label: 'Passed', color: '#14b36e', type: 'line' },
                  { key: 'failed', label: 'Failed', color: '#d03b3b', type: 'line' },
                  { key: 'skipped', label: 'Skipped', color: '#9CA3AF', type: 'line' },
                ]}
                xKey="period"
                height={300}
                chartType="line"
                width="100%"
              />
            </Box>
          </SectionCard>

          {/* Failing suites + recent failures */}
          <Grid templateColumns={{ base: '1fr', lg: '1.3fr 1fr' }} gap={5}>
            <GridItem>
              <SectionCard>
                <Heading size="sm" mb={3} color="gray.900">Failing suites</Heading>
                {failingSuites.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">No failing suites 🎉</Text>
                ) : (
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr><Th>Suite</Th><Th isNumeric>Failing</Th><Th isNumeric>Flaky</Th><Th isNumeric>Pass rate</Th></Tr>
                      </Thead>
                      <Tbody>
                        {failingSuites.map((s) => (
                          <Tr key={s.module}>
                            <Td fontWeight="600" color="gray.900">{s.module}</Td>
                            <Td isNumeric color="status.fail" fontWeight="700">{s.failedCount}</Td>
                            <Td isNumeric color="status.flaky">{s.flakyCount || 0}</Td>
                            <Td isNumeric color="gray.600">{Math.round(s.passRate || 0)}%</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </SectionCard>
            </GridItem>
            <GridItem>
              <SectionCard>
                <Heading size="sm" mb={3} color="gray.900">Recent failures</Heading>
                {recentFailures.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">No recent failures 🎉</Text>
                ) : (
                  <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
                    {recentFailures.map((f, i) => (
                      <Box key={f.id || i} py={2.5}>
                        <Text fontSize="13px" fontWeight="600" color="gray.900" noOfLines={1}>{f.testName}</Text>
                        <Text fontSize="11px" color="gray.400">{f.group}</Text>
                        <Text fontSize="12px" color="status.fail" fontFamily="mono" noOfLines={1}>{f.error}</Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </SectionCard>
            </GridItem>
          </Grid>
        </VStack>
      )}
    </Page>
  );
}
